import { Save } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Divider, IconButton, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { StyledActionButton } from 'src/components/styled/ActionButton';
import { StyledDiscardButton } from 'src/components/styled/DiscardButton';
import { extractPlannedSales } from 'src/components/views/Turnover/data/turnover';
import { months } from 'src/domain';
import { useTurnoverQuery } from 'src/hooks/turnover/queries/useTurnoverQuery';
import { useTurnoverSimulationRows } from 'src/hooks/turnover/simulation/useTurnoverSimulationRows';
import { formatDateToMonthKey } from 'src/utils/monthKeys';
export type BulkEditRow = {
    monthKey: number;
    value: number | undefined;
};

interface Props {
    isSaving: boolean;
    onCancel: () => void;
    onSave: () => void;
    onShowDetails: () => void;
    onChanges: (updatedChanges: BulkEditRow[], originalTotalSales: number, updatedTotalSales: number) => void;
}

export const BulkEditDrawerSection: FC<Props> = ({ isSaving, onCancel, onSave, onShowDetails, onChanges }) => {
    const { data: turnoverData } = useTurnoverSimulationRows();
    const { numberOfStores } = useTurnoverQuery() ?? {};
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const nextYear = new Date(new Date().setFullYear(currentYear + 1)).getFullYear();
    const currentMonth = currentDate.getMonth();

    const monthList = useMemo(() => {
        const list: Date[] = [];
        for (let i = 0; i < 18; i++) {
            const nextMonth = new Date(new Date().setMonth(currentMonth + i));

            if (nextMonth.getMonth() === 12) {
                list.push(new Date(nextYear, 0, 1));
            } else {
                list.push(new Date(currentYear, new Date().getMonth() + i, 1));
            }
        }
        return list;
    }, [currentMonth, currentYear, nextYear]);

    const yearList = useMemo(() => {
        return [currentYear, nextYear];
    }, [currentYear, nextYear]);

    const unEditedRows = useMemo(() => {
        return monthList.reduce<Record<string, BulkEditRow[]>>((acc, month) => {
            const monthKey = formatDateToMonthKey(month);
            if (acc[month.getFullYear()]) {
                return {
                    ...acc,
                    [month.getFullYear()]: [...acc[month.getFullYear()], { monthKey: monthKey, value: undefined }],
                };
            }
            return {
                ...acc,
                [month.getFullYear()]: [{ monthKey: monthKey, value: undefined }],
            };
        }, {});
    }, [monthList]);

    const [allValue, setAllValue] = useState<number | undefined>(0);
    const [rows, setRows] = useState<Record<string, BulkEditRow[]>>(unEditedRows);
    const changes = useMemo(() => {
        return Object.keys(rows).reduce<BulkEditRow[]>((acc, year) => {
            return [...acc, ...rows[year].filter((row) => row.value !== 0 && row.value !== undefined)];
        }, []);
    }, [rows]);

    const originalTotalSales = useMemo(() => {
        const sales = extractPlannedSales(turnoverData);
        if (!sales) return 0;
        return sales?.reduce((acc, curr) => acc + curr, 0);
    }, [turnoverData]);

    const updatedTotalSales = useMemo(() => {
        return turnoverData.reduce((acc, row) => {
            const value = changes.find((change) => change.monthKey === row.monthKey)?.value;
            let percentage = 100;

            if (value) percentage = 100 + Number(value);
            percentage = percentage / 100;

            if (!value) return acc + row.plannedSalesIv;
            return acc + row.plannedSalesIv * percentage;
        }, 0);
    }, [changes, turnoverData]);

    const hasUnsavedChanges = useMemo(() => {
        return changes.length > 0;
    }, [changes]);

    const handleCancel = useCallback(() => {
        setRows(unEditedRows);
        setAllValue(0);
        onCancel();
    }, [onCancel, unEditedRows]);

    const handleEditAll = (newValue: number | undefined) => {
        setRows(
            Object.keys(rows).reduce<Record<string, BulkEditRow[]>>((acc, year) => {
                return {
                    ...acc,
                    [year]: rows[year].map((row) => ({ ...row, value: newValue })),
                };
            }, {})
        );
    };

    useEffect(() => {
        onChanges(changes, originalTotalSales, updatedTotalSales);
    }, [changes, onChanges, originalTotalSales, updatedTotalSales]);

    return (
        <Stack
            sx={{
                gap: 4,
                width: 330,
                px: 6,
                py: 4,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Typography variant="h6">Bulk Edit</Typography>
                <IconButton edge="end" onClick={handleCancel}>
                    <CloseIcon />
                </IconButton>
            </Box>
            <Stack
                direction="column"
                sx={{
                    gap: 2,
                }}
            >
                <Stack
                    direction="row"
                    sx={{
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography variant="subtitle2">All</Typography>
                    <TextField
                        value={allValue}
                        onChange={(e) => {
                            if (e.target.value === '') return;
                            const parsedValue = Number.parseInt(e.target.value);
                            if (!Number.isNaN(parsedValue)) {
                                setAllValue(parsedValue);
                                handleEditAll(parsedValue);
                            }
                            if (e.target.value === '-' && allValue !== undefined) {
                                setAllValue(undefined);
                                handleEditAll(undefined);
                            }
                        }}
                        slotProps={{
                            input: {
                                sx: { width: '90px' },
                                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                            },

                            htmlInput: { style: { textAlign: 'right' } },
                        }}
                    />
                </Stack>
                {yearList.map((year) => (
                    <React.Fragment key={year}>
                        <Box
                            sx={{
                                py: 4,
                            }}
                        >
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    textAlign: 'center',
                                }}
                            >
                                {year}
                            </Typography>
                            <Divider />
                        </Box>
                        {monthList.map((month) => (
                            <React.Fragment key={formatDateToMonthKey(month)}>
                                {month.getFullYear() === year && (
                                    <Stack
                                        key={formatDateToMonthKey(month)}
                                        direction="row"
                                        sx={{
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <Typography variant="subtitle2">{months[month.getMonth()]}</Typography>

                                        <TextField
                                            value={
                                                rows[year]?.find((row) => row.monthKey === formatDateToMonthKey(month))
                                                    ?.value ?? 0
                                            }
                                            onChange={(e) => {
                                                if (e.target.value === '') return;
                                                setRows({
                                                    ...rows,
                                                    [year]: rows[year].map((row) => {
                                                        if (row.monthKey === formatDateToMonthKey(month)) {
                                                            return {
                                                                ...row,
                                                                value: e.target.value,
                                                            };
                                                        }
                                                        return row;
                                                    }),
                                                });
                                            }}
                                            slotProps={{
                                                input: {
                                                    sx: { width: '90px' },
                                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                                },

                                                htmlInput: { style: { textAlign: 'right' } },
                                            }}
                                        />
                                    </Stack>
                                )}
                            </React.Fragment>
                        ))}
                    </React.Fragment>
                ))}
            </Stack>
            <Box>
                <Box
                    sx={{
                        py: 4,
                    }}
                >
                    <Typography
                        variant="subtitle1"
                        sx={{
                            textAlign: 'center',
                        }}
                    >
                        Stores affected
                    </Typography>
                    <Divider />
                </Box>

                <Stack
                    direction="row"
                    sx={{
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography>
                        <b>{numberOfStores}</b> stores will be affected by changes
                    </Typography>
                    <Button color="info" variant="text" onClick={onShowDetails}>
                        See details
                    </Button>
                </Stack>
            </Box>
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'flex-end',
                }}
            >
                <StyledDiscardButton size="medium" variant="text" onClick={handleCancel}>
                    Cancel
                </StyledDiscardButton>
                <StyledActionButton
                    disabled={!hasUnsavedChanges}
                    loading={isSaving}
                    size="medium"
                    variant="contained"
                    color="info"
                    startIcon={<Save />}
                    onClick={onSave}
                >
                    Save
                </StyledActionButton>
            </Box>
        </Stack>
    );
};
